import { Popover, PopoverContent, PopoverTrigger, Portal } from '@chakra-ui/react';
import { ShareOneIcon } from '@sketchnote/icons';
import { Button, LoaderXs, Panel, Tabs } from '@sketchnote/shared-components';
import * as React from 'react';

const { useState, useRef } = React;

export const EmbedPopup = ({ onSubmit }: { onSubmit: (url: string) => void }) => {
  const [isOpenMenu, setIsOpenMenu] = useState(false);
  const [embedUrl, setEmbedUrl] = useState('');
  const embedInputRef = useRef<HTMLInputElement>(null);
  // We need loading state soon to check/fetch the embed url using apis
  const [isLoading] = useState(false);
  const toggleMenu = (value?: boolean) => () => {
    setIsOpenMenu(value ?? !isOpenMenu);
    if (embedInputRef) {
      setTimeout(() => {
        embedInputRef.current?.focus();
      }, 200);
    }
  };

  const handleUrlChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setEmbedUrl(e.target.value);
  };

  const checkAndSubmit = (checkUrl: string) => {
    const youtubeEmbedUrl = getYoutubeEmbedUrl(checkUrl);
    if (youtubeEmbedUrl) {
      onSubmit(youtubeEmbedUrl);
      return;
    }
    const figmaEmbedUrl = getFigmaCode(checkUrl);
    if (figmaEmbedUrl) {
      onSubmit(figmaEmbedUrl);
      return;
    }
    const vimeoEmbedUrl = getVimeoUrl(checkUrl);
    if (vimeoEmbedUrl) {
      onSubmit(vimeoEmbedUrl);
      return;
    }
    const loomEmbedUrl = getLoomUrl(checkUrl);
    if (loomEmbedUrl) {
      onSubmit(loomEmbedUrl);
      return;
    }
    const airtableEmbedUrl = getAirtableUrl(checkUrl);
    if (airtableEmbedUrl) {
      onSubmit(airtableEmbedUrl);
      return;
    }
    onSubmit(checkUrl);
  };

  const handleOnSubmit = (e: any) => {
    e.preventDefault();
    const errorMessage = 'Enter a valid url';
    if (isValidUrl(embedUrl)) {
      checkAndSubmit(embedUrl);
    } else {
      const protocolCheck = /^(https?):\/\/.+/;
      if (!protocolCheck.test(embedUrl)) {
        const embedUrlWithProtocol = `//${embedUrl}`;
        if (isValidUrl(embedUrlWithProtocol)) {
          checkAndSubmit(embedUrlWithProtocol);
        } else {
          window.alert(errorMessage);
        }
      } else {
        window.alert(errorMessage);
      }
    }
  };

  return (
    <Popover
      placement="bottom"
      isOpen={isOpenMenu}
      onOpen={toggleMenu(true)}
      onClose={toggleMenu(false)}
      modifiers={[{ name: 'eventListeners', options: { scroll: isOpenMenu } }]}
    >
      <PopoverTrigger>
        <button className="px-16 py-10 w-full flex bg-gray-100 hover:bg-gray-200 items-center text-gray-500">
          {isLoading ? (
            <LoaderXs />
          ) : (
            <React.Fragment>
              <ShareOneIcon />
              <span className="ml-8">Embed anything..</span>
            </React.Fragment>
          )}
        </button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent>
          {!isLoading ? (
            <form
              onSubmit={handleOnSubmit}
              className="flex flex-col p-16 rounded-lg bg-white shadow-sm text-gray-999 min-w-400"
            >
              <Tabs selected={0} size="small" className="border-b">
                <Panel title="Embed link">
                  <div className="my-16">
                    <input
                      className="px-12 py-13 border border-gray-200 w-full rounded-md mb-16 bg-gray-100 placeholder-gray-500"
                      onChange={handleUrlChange}
                      ref={embedInputRef}
                      placeholder="Paste https://..."
                    />
                    <Button type="submit" className="justify-center w-full">
                      Embed link
                    </Button>
                  </div>
                </Panel>
              </Tabs>
            </form>
          ) : null}
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

function isValidUrl(value: string) {
  return /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(
    value
  );
}

function getYoutubeEmbedUrl(url: string) {
  const pattern =
    /^(?:https?:\/\/)?(?:m\.|www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;
  const match = url.match(pattern);
  if (match?.[1]) {
    return `https://www.youtube.com/embed/${match?.[1]}`;
  }
  return false;
}

function getFigmaCode(url: string) {
  const pattern =
    /^(?:https:\/\/)?(?:www\.)?figma\.com\/(file|proto)\/([0-9a-zA-Z]{22,128})(?:\/.*)?node-id=([^&]*)(?:&t=.*)?$/;
  const match = url.match(pattern);
  if (match?.[0] && match?.[2]) {
    return `https://www.figma.com/embed?embed_host=sketchnote.co&url=${match[0]}`;
  }
  return false;
}

function getVimeoUrl(url: string) {
  const pattern = /https:\/\/(www\.)?vimeo.com\/(\d+)($|\/)/;
  const match = url.match(pattern);
  if (match?.[2]) {
    return `https://player.vimeo.com/video/${match[2]}`;
  } else {
    return false;
  }
}

function getLoomUrl(url: string) {
  const pattern = /https:\/\/(www\.)?loom.com\/share\/([0-9a-z]{32,32})($|\/)/;
  const match = url.match(pattern);
  if (match?.[2]) {
    return `https://www.loom.com/embed/${match[2]}`;
  } else {
    return false;
  }
}

function getAirtableUrl(url: string) {
  const pattern = /(?:https:\/\/)?(?:www\.)?airtable\.com\/(.*)/;
  const match = url.match(pattern);
  if (match?.[1]) {
    return `https://airtable.com/embed/${match[1]}`;
  } else {
    return false;
  }
}
